import { useMutation, useQuery } from "@tanstack/react-query";
import { getEntity } from "@brasilnovo/shared";
import { api } from "../lib/api.js";
import { Modal } from "./Modal.js";
import { FormEngine } from "./FormEngine.js";
import { ErrorBox, Spinner } from "./ui.js";

/**
 * Criar uma ordem de coleta do zero ou editar uma existente, direto da Torre e
 * do Vincular carga. A VEGA chega pela API do Arthur, mas as cargas da
 * USIMINAS são lançadas à mão — e a torre não achava onde (out/2026: o
 * formulário só existia em Operações, como "Nova viagem").
 *
 * `inicial.id` presente = edição: relê a linha completa (os cartões e a grade
 * trazem só parte das colunas) e grava por PATCH parcial. Sem id = criação,
 * com empresa/competência já preenchidas pelo contexto da tela.
 */
export function OrdemColetaModal({ inicial, onClose, onSalvo }: {
  inicial: Record<string, unknown>;
  onClose: () => void;
  onSalvo: () => void;
}) {
  const id = inicial.id as number | undefined;
  const completa = useQuery({
    queryKey: ["viagem-completa", id],
    enabled: id != null,
    queryFn: async () => (await api.get(`/entities/viagem_vega/${id}`)).data as Record<string, unknown>,
  });
  const salvar = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      id != null
        ? (await api.patch(`/entities/viagem_vega/${id}`, data)).data
        : (await api.post("/entities/viagem_vega", data)).data,
    onSuccess: onSalvo,
  });

  // A competência da tela vem como "2026-09" ou "2026-09-01"; o campo é data (1º dia do mês).
  const comp = typeof inicial.competencia === "string" ? inicial.competencia.slice(0, 7) : "";
  const novos = { ...inicial, competencia: /^\d{4}-\d{2}$/.test(comp) ? `${comp}-01` : undefined };
  const valores = id != null ? completa.data : Object.fromEntries(Object.entries(novos).filter(([, v]) => v != null && v !== ""));
  const titulo = id != null ? `Editar ordem ${String(inicial.numero_ordem ?? `#${id}`)}` : "Nova ordem de coleta";

  return (
    <Modal title={titulo} onClose={onClose}>
      {id == null && (
        <p className="mb-3 rounded-lg bg-brand-50 px-3 py-2 text-xs text-slate-600" data-testid="nova-ordem-ajuda">
          Use para as cargas lançadas à mão (ex.: <b>USIMINAS</b>). Sem motorista, a ordem entra em <b>disponíveis</b>;
          com motorista, vai direto para <b>agendadas</b>. As cargas da VEGA continuam chegando pela API.
        </p>
      )}
      {salvar.isError && <div className="mb-3"><ErrorBox error={salvar.error} /></div>}
      {id != null && completa.isError ? <ErrorBox error={completa.error} />
        : !valores ? <Spinner />
        : (
          <FormEngine
            spec={getEntity("viagem_vega")!}
            initial={valores}
            mode={id != null ? "update" : "create"}
            onSubmit={(d) => salvar.mutate(d)}
            onCancel={onClose}
            submitting={salvar.isPending}
          />
        )}
    </Modal>
  );
}
